/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Attributes from "model/stats/Attributes";
import { computeSavingThrows, computeHitDice } from "character/CharacterCalc";
import Monster from "model/monster/Monster";
import Utils from "utils/Utils";
import Open5e from "model/formats/Open5e";
import ClassService from "services/ClassService";
import RaceService from "services/RaceService";
import Race from "model/character/Race";
import SkillStats from "model/stats/SkillStats";
import CharacterService from "services/CharacterService";
import SpellService from "services/SpellService";
import Section from "model/abilities/Section";
import Spell from 'model/stats/Spell';
import NpcData from "model/monster/NpcData";
import SkillSet, { Skill } from "model/stats/SkillSet";
import AttributeValues from "model/stats/AttributeValues";
import Background from "model/character/Background";
import { ClassDetails } from "model/character/ClassDetails";
import Armor from "model/stats/Armor";
import Abilities from "model/abilities/Abilities";

export const mapToString = (map: Map<string, string[]>): string => {
  const results = [];
  for (const key of map.keys()) {
    const valString = map.get(key)?.join(':') || '';
    results.push(`${key.trim()}:${valString}`);
  }
  return results.join("|");
}

export const stringToMap = (str: string): Map<string, string[]> => {
  const results = new Map();
  for (const entry of (str?.split('|') || [])) {
    const values = entry.split(':');
    if (values.length > 1) {
      const [name, ...valueArray] = values;
      results.set(name.trim(), valueArray);
    }
  }
  return results;
}

export interface BuildMonsterParams {
  npcName: string;
  selectedRace: Race | null;
  selectedBackground: Background | null;
  primaryClassDetails: ClassDetails;
  secondaryClassDetails: ClassDetails;
  attributeBases: number[];
  diceArray: string;
  attributeValues: AttributeValues;
  asiRacialSelected: string[];
  asiClassSelected: string[];
  raceOptionsSelectedArrayByOptionNameMap: Map<string, string[]>;
  langsRaceSelected: string[];
  langsClassSelected: string[];
  langsBackgroundSelected: string[];
  skillsRaceSelected: string[];
  skillsClassSelected: string[];
  skillsBackgroundSelected: string[];
  equipmentRaceSelected: string[];
  equipmentClassSelected: string[];
  equipmentBackgroundSelected: string[];
  featsByName: Map<string, Ability>;
  spellsBySpellLevelByClass: Map<string, Map<number, Spell[]>>;
  equipedArmor: Armor | null;
  equipedArmorClass: number;
  equipedArmorDesc: string;
  equipedWeapon1: string;
  equipedWeapon2: string;
  equipedWeapon3: string;
}

export function buildMonsterFromCharacter(params: BuildMonsterParams): Monster {
  const {
    npcName,
    selectedRace,
    selectedBackground,
    primaryClassDetails,
    secondaryClassDetails,
    attributeBases,
    diceArray,
    attributeValues,
    asiRacialSelected,
    asiClassSelected,
    raceOptionsSelectedArrayByOptionNameMap,
    langsRaceSelected,
    langsClassSelected,
    langsBackgroundSelected,
    skillsRaceSelected,
    skillsClassSelected,
    skillsBackgroundSelected,
    equipmentRaceSelected,
    equipmentClassSelected,
    equipmentBackgroundSelected,
    featsByName,
    spellsBySpellLevelByClass,
    equipedArmor,
    equipedArmorClass,
    equipedArmorDesc,
    equipedWeapon1,
    equipedWeapon2,
    equipedWeapon3,
  } = params;

  if (!primaryClassDetails.classInfo) {
    throw new Error('buildMonsterFromCharacter requires primaryClassDetails.classInfo');
  }

  const combinedLevel: number = primaryClassDetails.classLevel + (secondaryClassDetails.classInfo ? (secondaryClassDetails.classLevel || 0) : 0);
  const hitDiceBonus: number = Attributes.getBonus(attributeValues.constitution) * combinedLevel;
  const { hitPoints, hitDice, hitDiceType } = computeHitDice({
    primaryHitDie: parseInt(primaryClassDetails.classInfo.hit_dice.split('d')[1]),
    primaryLevel: primaryClassDetails.classLevel,
    hitDiceBonus,
    secondaryHitDie: secondaryClassDetails.classInfo ? parseInt(secondaryClassDetails.classInfo.hit_dice.split('d')[1]) : null,
    secondaryLevel: secondaryClassDetails.classLevel,
  });
  const senses = RaceService.parseSenses(selectedRace);
  let npcDesc = `${selectedBackground?.name ?? ''} ${selectedRace?.name ?? ''} ${selectedBackground || selectedRace ? ':' : ''} **${primaryClassDetails.classInfo.name}**-${primaryClassDetails.classLevel}`;
  if (primaryClassDetails.archetype) {
    npcDesc += ` (${primaryClassDetails.archetype.name})`;
  }
  if (secondaryClassDetails.classInfo) {
    npcDesc += ` / **${secondaryClassDetails.classInfo.name}**-${secondaryClassDetails.classLevel}`;
  }
  if (secondaryClassDetails.archetype) {
    npcDesc += ` (${secondaryClassDetails.archetype.name})`;
  }
  let sections: Section[] = [new Section('', npcDesc)];
  const langs = [...langsRaceSelected, ...langsClassSelected, ...langsBackgroundSelected];
  const speed = RaceService.parseSpeed(selectedRace);
  const size  = RaceService.parseSize(selectedRace);
  RaceService.processRace(selectedRace, raceOptionsSelectedArrayByOptionNameMap, sections);
  ClassService.processClass(primaryClassDetails, sections);
  if (secondaryClassDetails) {
    ClassService.processClass(secondaryClassDetails, sections);
  }
  for (let index=0 ; index<asiClassSelected.length ; index++) {
    const feat = featsByName.get(asiClassSelected[index]);
    if (feat) {
      sections.push(new Section( feat.name, feat.desc));
    }
  }

  Section.simplifySections(sections);

  const hitPointsCustom = `${hitPoints} (${hitDice})`;
  const proficiencyBonus = Utils.getProfBonusByCR(combinedLevel);
  //const proficientToolsArray = CharacterService.splitItems(selectedBackground?.tool_proficiencies ?? null, []).sort();
  let equipmentArray = CharacterService.splitItems(selectedBackground?.equipment ?? null, []);

  // remove duplicates:
  const allSkills = [... new Set([...skillsRaceSelected, ...skillsClassSelected, ...skillsBackgroundSelected])];
  // Not all the skills selected are in the skills set for monsters. As we identify these, we'll put them in an abilities block:
  const overflowProfSkills: string[] = [];
  const overflowExpertSkills: string[] = [];
  let skillSet = new SkillSet();
  for (const skName of allSkills) {
    const isExpert = skName.endsWith(SkillStats.EXPERT);
    const skillName = isExpert ? skName.replace(SkillStats.EXPERT, '') : skName;
    const attribute = SkillStats.getSkillsMap().get(skillName);
    if (attribute) {
      const skill = skillSet.skills[skillName];
      if (!skill) {
        const skill = new Skill();
        skill.attribute = attribute;
        skill.level = isExpert ? 'expert' : 'proficient';
        skillSet.skills[skillName] = skill;
      } else {
        // avoid downgrading a skill from expert to proficient:
        skill.level = isExpert ? 'expert' : skill.level;
      }
    } else {
      if (isExpert) {
        overflowExpertSkills.push(skillName);
        const index = overflowProfSkills.indexOf(skillName);
        if (index >= 0) {
          overflowProfSkills.splice(index, 1);
        }
      } else {
        if (!overflowExpertSkills.includes(skillName)) {
          overflowProfSkills.push(skillName);
        }
      }
    }
  }
  skillSet = SkillSet.updateLevels(skillSet, attributeValues, proficiencyBonus);

  const desc = [];
  if (overflowProfSkills.length) {
    desc.push(`* **Proficient:** ${overflowProfSkills.join(', ')}`);
  }
  if (overflowExpertSkills.length) {
    desc.push(`* **Expert:** ${overflowExpertSkills.join(', ')}`);
  }
  // if (proficientToolsArray.length) {
  //   desc.push(`* **Tools:** ${proficientToolsArray.join(', ')}`);
  // }
  if (desc.length) {
    sections.push(new Section('Additional Skills', desc.join('\n')));
  }

  equipmentArray = CharacterService.splitItems(primaryClassDetails.classInfo.equipment, equipmentArray);
  // secondary classes don't grant equipment

  const bonus_actions: Section[] = sections.filter(section => Section.isBonusAction(section));
  sections = sections.filter(section => !Section.isBonusAction(section));
  const reactions: Section[] = sections.filter(section => Section.isReaction(section));
  sections = sections.filter(section => !Section.isReaction(section));
  const spellSections: Section[] = sections.filter(section => Section.isSpell(section));
  sections = sections.filter(section => !Section.isSpell(section));

  const damageResistancesArray = [];
  // convert any damage resistances into the damage resistance array:
  for (let index=0 ; index<sections.length ; index++) {
    const desc = sections[index].desc.toLowerCase();
    if (desc.includes('you have resistance')) {
      for (const res of Utils.damageTypes) {
        if ((desc.includes(`you have resistance to ${res.toLowerCase()}`)) ||
            (desc.includes(`you have resistance against ${res.toLowerCase()}`))) {
            damageResistancesArray.push(res);
          if (desc == `you have resistance to ${res.toLowerCase()}.`) {
            sections.splice(index, 1);
            index--;
          }
        }
      }
    }
  }
  if (selectedBackground?.feature && selectedBackground?.feature_desc) {
    sections.push(new Section(selectedBackground.feature, selectedBackground.feature_desc));
  }

  sections = Section.removeAbilitiesThatHaveBeenHandled(sections);

  // validate spells & options for each class:
  if (primaryClassDetails) {
    primaryClassDetails.validateSpells(spellsBySpellLevelByClass);
    primaryClassDetails.validateSelectedOptions();
  }
  if (secondaryClassDetails) {
    secondaryClassDetails.validateSpells(spellsBySpellLevelByClass);
    secondaryClassDetails.validateSelectedOptions();
  }
  const spellSlots = SpellService.getSpellSlots(primaryClassDetails, secondaryClassDetails);
  const warlockSpellSlots = SpellService.getWarlockSpellSlots(primaryClassDetails.classInfo, primaryClassDetails.classLevel) ||
                            SpellService.getWarlockSpellSlots(secondaryClassDetails.classInfo || null, secondaryClassDetails.classLevel || 0);

  SpellService.addSpells(spellSections, primaryClassDetails, secondaryClassDetails, spellSlots, warlockSpellSlots);

  // This Challenge Rating is a bit of a guess. a 20th level PC should be around CR 12, so I assume a 3/5 conversion.
  // But it wont work accurately across the board.
  const cr = Math.round(combinedLevel * 3 / 5);
  const savingProfs = computeSavingThrows(primaryClassDetails.classInfo.prof_saving_throws, attributeValues, proficiencyBonus);
  const percSkill = skillSet.skills['Perception'];
  const passivePerception = 10 + (percSkill?.adjLevel ?? Attributes.getBonus(attributeValues.wisdom));

  const mon: Monster = new Monster();
  mon.file_type          = 'dnd monster';
  mon.inventory          = [];
  mon.hitDiceNumber      = combinedLevel;
  mon.hitDiceType        = hitDiceType;
  mon.hitDiceBonus       = hitDiceBonus;
  mon.hitPointsCustom    = hitPointsCustom;
  mon.usePcHitDice       = true;
  mon.shortName          = "";
  mon.pluralName         = "";
  mon.name               = npcName;
  mon.size               = size;
  mon.type               = "Humanoid";
  mon.subtype            = selectedRace?.name ?? '';
  mon.hit_points         = hitPoints;
  mon.hit_dice           = hitDice;
  mon.speed              = speed;
  mon.attributes         = attributeValues;
  mon.skillSet           = skillSet;
  mon.senses             = senses;
  mon.languages          = langs.join(', ');
  mon.challenge_rating   = "" + cr;
  mon.cr                 = cr;
  mon.proficiencyBonus   = proficiencyBonus;
  mon.xp                 = Utils.getXpByCR(combinedLevel);
  mon.passivePerception  = passivePerception;
  mon.traits             = sections;
  mon.spells             = spellSections;
  mon.damage_resistances = damageResistancesArray.join(", ");
  mon.bonus_actions      = bonus_actions;
  mon.reactions          = reactions;
  mon.saving_throws      = savingProfs.join(', ');
  mon.armor              = equipedArmor;
  mon.armor_class        = equipedArmorClass;
  mon.armor_desc         = equipedArmorDesc;

  let gear: string[] = [...equipmentRaceSelected, ...equipmentClassSelected, ...equipmentBackgroundSelected];
  if (equipedArmor) {
    // If we selected an armor type, dont list any armor we got from our class/race/background
    gear = gear.filter(g => !Armor.getArmorNames().map(a => a.toLowerCase()).includes(g.toLowerCase()));
    gear = gear.filter(g => g.toLowerCase() !== 'shield')
               .map(a => a.toLowerCase().startsWith('a shield and') ? a.substring('a shield and'.length) : a.replace(' and a shield', ''));
    if (equipedArmorDesc?.includes('Shield')) {
      gear.unshift('Shield');
    }
    gear.unshift(equipedArmor.name);
  }
  for (const equipedWeapon of [equipedWeapon3, equipedWeapon2, equipedWeapon1]) {
    if (equipedWeapon) {
      let abilites = Abilities.weapons.filter(w => (w.name?.toLowerCase() === equipedWeapon.toLowerCase()));
      if (abilites.length === 2) {
        if (abilites[0].weapon_data?.attribute === 'finese') {
          const attr = (attributeValues.dexterity > attributeValues.strength) ? 'dexterity' : 'strength';
          abilites = abilites.filter(w => w.attribute?.toLowerCase() === attr);
        }
      }
      if (abilites.length === 1) {
        mon.actions.unshift(abilites[0]);
        gear.unshift(abilites[0].name);
      }
    }
  }
  mon.gear = gear.join(', ');

  // store the NPC data, so this data can be editted
  const raceOptions = mapToString(raceOptionsSelectedArrayByOptionNameMap);
  const primaryClassOptions = mapToString(primaryClassDetails.optionsSelectedArrayByOptionNameMap);
  const secondaryClassOptions = mapToString(secondaryClassDetails.optionsSelectedArrayByOptionNameMap || new Map());
  const npcData: NpcData = {
                  name: npcName,
                  raceName: selectedRace?.name ?? '',
                  raceOptions, attributeBases, diceArray, attributeValues, asiRacialSelected: [...asiRacialSelected],
                  asiClassSelected: [...asiClassSelected],
                  backgroundName: selectedBackground?.name ?? '',
                  primaryClassName: primaryClassDetails.classInfo.name,
                  primaryArchetypeName: primaryClassDetails.archetype?.name || '',
                  primaryLevel: primaryClassDetails.classLevel,
                  primaryClassOptions,
                  secondaryClassName: secondaryClassDetails.classInfo?.name || '',
                  secondaryArchetypeName: secondaryClassDetails.archetype?.name || '',
                  secondaryLevel: secondaryClassDetails.classLevel || 0,
                  secondaryClassOptions,
                  langsRaceSelected: [...langsRaceSelected],
                  langsClassSelected: [...langsClassSelected],
                  langsBackgroundSelected: [...langsBackgroundSelected],
                  skillsRaceSelected: [...skillsRaceSelected],
                  skillsClassSelected: [...skillsClassSelected],
                  skillsBackgroundSelected: [...skillsBackgroundSelected],
                  spellbookSpellsArrayBySpellLevelPrimary: [...primaryClassDetails.spellbookSpellsArrayBySpellLevel],
                  spellbookSpellsArrayBySpellLevelSecondary: [...(secondaryClassDetails.spellbookSpellsArrayBySpellLevel || [])],
                  spellsPreparedArrayBySpellLevelPrimary: [...primaryClassDetails.spellsPreparedArrayBySpellLevel],
                  spellsPreparedArrayBySpellLevelSecondary: [...(secondaryClassDetails.spellsPreparedArrayBySpellLevel || [])],
                  spellbookSpellKeysBySpellLevelPrimary:   NpcData.getKeysFromSpells(primaryClassDetails.spellbookSpellsArrayBySpellLevel),
                  spellbookSpellKeysBySpellLevelSecondary: NpcData.getKeysFromSpells(secondaryClassDetails.spellbookSpellsArrayBySpellLevel),
                  spellKeysPreparedBySpellLevelPrimary:    NpcData.getKeysFromSpells(primaryClassDetails.spellsPreparedArrayBySpellLevel),
                  spellKeysPreparedBySpellLevelSecondary:  NpcData.getKeysFromSpells(secondaryClassDetails.spellsPreparedArrayBySpellLevel),
                  primaryAttribute:   '',
                  secondaryAttribute: '',
                };
  mon.npcData = npcData;
  Open5e.computeSplitPoint(mon);
  return mon;
}
